// @ts-strict-ignore
import { Connection } from '@3d-scene/model3d/connection';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { Box3, Mesh, Object3D, Plane, Vector3 } from 'three';
import { MetricUtils } from '@3d-scene/common/util/metric-utils';
import { ContainerService } from '../model3d/container.service';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { ArticleContainer } from '../model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { PlaneClassification } from '@3d-scene/model/plane-classification';
import { Face3D } from '@3d-scene/model/face-3d';
import { ConnectionInfoUtils } from '../model3d/util/connection-info-utils';
import { IsThreeJsType } from '../common/util/is-threejs-type';

export class CollisionHelper {
  private static isAABBCollisionByBoxes(source: Box3, target: Box3, useEpsilonToAvoidCollision: boolean = false, useEpsilonToCauseCollision: boolean = false): boolean {
    return this.isAABBCollisionByVectors(source.min, source.max, target.min, target.max, useEpsilonToAvoidCollision);
  }

  private static isAABBCollisionByVectors(
    sourceMin: Vector3,
    sourceMax: Vector3,
    targetMin: Vector3,
    targetMax: Vector3,
    useEpsilonToAvoidCollision: boolean = false,
    useEpsilonToCauseCollision: boolean = false
  ): boolean {
    const excludeEpsilon: number = useEpsilonToAvoidCollision ? CommonConstants.EPSILON : 0;
    const includeEpsilon: number = useEpsilonToCauseCollision ? CommonConstants.EPSILON : 0;
    if (targetMin.x + excludeEpsilon > sourceMax.x + includeEpsilon || sourceMin.x + excludeEpsilon > targetMax.x + includeEpsilon) {
      return false;
    }
    if (targetMin.y + excludeEpsilon > sourceMax.y + includeEpsilon || sourceMin.y + excludeEpsilon > targetMax.y + includeEpsilon) {
      return false;
    }
    if (targetMin.z + excludeEpsilon > sourceMax.z + includeEpsilon || sourceMin.z + excludeEpsilon > targetMax.z + includeEpsilon) {
      return false;
    }

    return true;
  }

  /**
   * Returns all active connections of all articles in 3D scene, which collide with
   * the bounding box determined by the provided first and last connection.
   * If first/last connections are active, then they are included as first/last element in the returned result.
   *
   * If ignoreYPosition is set to true, then a connection will cause a collition, no matter what its y position is.
   * If ignoreZPosition is set to true, then a connection will cause a collition, no matter what its z position is.
   * If onlyUseMiddlePointOfConnections is set to true, then only the middle point of a connection will cause a collition, otherwise the complete area of a connection will cause a collision
   */
  public static findCollisionConnectionsInGlobal(
    firstConnection: Connection,
    lastConnection: Connection,
    containerService: ContainerService,
    useEpsilonToCauseCollision: boolean = true,
    ignoreYPosition: boolean = false,
    ignoreZPosition: boolean = false,
    onlyUseMiddlePointOfConnections: boolean = true
  ): Connection[] {
    const min: Vector3 = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    const max: Vector3 = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    FaceUtils.findMinMaxForMesh(firstConnection.mesh, min, max, true);
    FaceUtils.findMinMaxForMesh(lastConnection.mesh, min, max, true);
    if (ignoreYPosition) {
      min.y = -Number.MAX_VALUE;
      max.y = Number.MAX_VALUE;
    }
    if (ignoreZPosition) {
      min.z = -Number.MAX_VALUE;
      max.z = Number.MAX_VALUE;
    }
    const collisionConnections: Connection[] = this.findCollisionConnectionsInGlobalByMinMax(
      containerService,
      min,
      max,
      useEpsilonToCauseCollision,
      onlyUseMiddlePointOfConnections
    );
    const firstConnectionIndex: number = collisionConnections.indexOf(firstConnection);
    if (firstConnectionIndex > 0) {
      collisionConnections.splice(firstConnectionIndex, 1);
      collisionConnections.unshift(firstConnection);
    }
    const lastConnectionIndex: number = collisionConnections.indexOf(lastConnection);
    if (lastConnectionIndex > 0) {
      collisionConnections.splice(lastConnectionIndex, 1);
      collisionConnections.push(lastConnection);
    }
    return collisionConnections;
  }

  private static findCollisionConnectionsInGlobalByMinMax(
    containerService: ContainerService,
    min: Vector3,
    max: Vector3,
    useEpsilonToCauseCollision: boolean = true,
    onlyUseMiddlePointOfConnections: boolean = true
  ): Connection[] {
    const collisionConnections: Connection[] = [];
    for (const articleContainer of containerService.enumerateArticleContainersInGlobal()) {
      const connectionsToCheck: Connection[] = ConnectionInfoUtils.findConnectionsInArticles([articleContainer]);
      for (const connectionToCheck of connectionsToCheck) {
        if (!connectionToCheck.isActive) {
          continue;
        }
        if (onlyUseMiddlePointOfConnections) {
          const connectionMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(connectionToCheck.compositionRectangle);
          if (this.isAABBCollisionByVectors(connectionMiddlePoint, connectionMiddlePoint, min, max, false, useEpsilonToCauseCollision)) {
            collisionConnections.push(connectionToCheck);
          }
        } else {
          const connectionMinPoint: Vector3 = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
          const connectionMaxPoint: Vector3 = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
          FaceUtils.findMinMaxForRectangle(connectionToCheck.compositionRectangle, connectionMinPoint, connectionMaxPoint);
          if (this.isAABBCollisionByVectors(connectionMinPoint, connectionMaxPoint, min, max, false, useEpsilonToCauseCollision)) {
            collisionConnections.push(connectionToCheck);
          }
        }
      }
    }
    return collisionConnections;
  }

  /**
   * Checks collision between target container and each scene container.
   *
   * @param targetContainer target container
   * @param sceneContainers array of scene containers which need to check with target container
   * @param excludeConnections true if connection meshes are excluded from collision detection
   * @param useEpsilonToAvoidCollision true if epsilon should be used to avoid collision
   * @param inGlobal true to check the collision in the global coordinate system, false to check relative to target container.
   * @return true if collision was detected, otherwise false
   */
  public static isCollisionDetected(
    targetContainer: ArticleContainer,
    sceneContainers: ArticleContainer[],
    excludeConnections: boolean = false,
    useEpsilonToAvoidCollision: boolean = false,
    inGlobal: boolean = true
  ): boolean {
    if (!sceneContainers || sceneContainers.length === 0) {
      return false;
    }

    const targetBounds = this.createAxisAlignedBounds(targetContainer, excludeConnections, inGlobal);

    for (const container of sceneContainers) {
      const containerBounds = this.createAxisAlignedBounds(container, excludeConnections, inGlobal, targetContainer);
      const isCollision = this.isAABBCollisionByBoxes(containerBounds, targetBounds, useEpsilonToAvoidCollision, false);
      if (isCollision) {
        return true;
      }
    }

    return false;
  }

  private static createAxisAlignedBounds(container: ArticleContainer, excludeConnections: boolean = false, inGlobal: boolean = true, targetContainer: Object3D = null): Box3 {
    const excludedNames: string[] =
      excludeConnections
      ? container.article.metaElements.map((metaElement) => metaElement.name)
      : [];
    excludedNames.push(WireBridgeService.WIRECONTOUR_NAME);
    const relativeTo = inGlobal ? null : targetContainer;
    return MetricUtils.createBoundsRecursively(container, inGlobal, excludedNames, relativeTo);
  }

  /**
   * Returns faces of the given object that intersects plane.
   */
  public static findIntersectionFacesByPlane(object: Object3D, plane: Plane, articleContainerUtils: ArticleContainerUtils): Face3D[] {
    if (!articleContainerUtils.isObjectRelevant(object)) {
      return [];
    }

    let result: Face3D[] = [];
    if (IsThreeJsType.mesh(object)) {
      const faces: Face3D[] = FaceUtils.getFaces(object as Mesh);
      for (const face of faces) {
        const v0: Vector3 = face.vertices[0];
        const v1: Vector3 = face.vertices[1];
        const v2: Vector3 = face.vertices[2];

        if (this.isIntersectPoints(v0, v1, plane) || this.isIntersectPoints(v0, v2, plane) || this.isIntersectPoints(v1, v2, plane)) {
          result.push(face);
        }
      }
    }

    for (const child of Object3DUtils.getChildren(object)) {
      result = result.concat(this.findIntersectionFacesByPlane(child, plane, articleContainerUtils));
    }

    return result;
  }

  private static isIntersectPoints(p1: Vector3, p2: Vector3, plane: Plane): boolean {
    const classifyP1: number = MathUtils.classifyPoint(p1, plane);
    const classifyP2: number = MathUtils.classifyPoint(p2, plane);

    return (
      (classifyP1 === PlaneClassification.FRONT && classifyP2 === PlaneClassification.BACK) ||
      (classifyP1 === PlaneClassification.BACK && classifyP2 === PlaneClassification.FRONT)
    );
  }
}
